import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Modal
} from "shared/components";
import {
  getVendorAccountErrorCode,
  getVendorAccountErrorMessage,
  lockboxAccountsApi,
  VendorAccount
} from "api/lockbox-accounts";
import type { LockboxVendor } from "api/landlord/lockboxes/types";

interface RenameLockboxAccountModalProps {
  vendor: LockboxVendor;
  account: VendorAccount | null;
  existingAccounts: VendorAccount[];
  onClose: () => void;
}

export const RenameLockboxAccountModal = ({
  vendor,
  account,
  existingAccounts,
  onClose
}: RenameLockboxAccountModalProps) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState(account?.name ?? "");
  const [nameError, setNameError] = useState("");

  const { mutate: rename, isPending } = useMutation({
    mutationKey: ["LOCKBOX_ACCOUNTS", vendor, "UPDATE"],
    mutationFn: () =>
      lockboxAccountsApi.rename(vendor, account!.id, name.trim()),
    onSuccess: () => {
      toast.success("Credentials renamed");
      queryClient.invalidateQueries({
        queryKey: ["LOCKBOX_ACCOUNTS", vendor]
      });
      queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
      onClose();
    },
    onError: (err) => {
      const message = getVendorAccountErrorMessage(err);
      if (getVendorAccountErrorCode(err) === "duplicate_name")
        setNameError(message);
      else toast.error(message);
    }
  });

  const handleSubmit = () => {
    if (!account) return;
    const trimmed = name.trim();
    const duplicate = existingAccounts.some(
      (other) => other.id !== account.id && other.name === trimmed
    );
    if (duplicate) {
      setNameError("An account with this name already exists for this vendor");
      return;
    }
    rename();
  };

  return (
    <Modal
      opened={Boolean(account)}
      onClose={onClose}
      panelClassName="w-full max-w-[440px] p-6 space-y-6"
    >
      <h2 className="text-xl font-bold text-content-1000">
        Rename credentials
      </h2>
      <BaseInput
        label="Account name"
        required
        id={`${vendor}-rename-account-name`}
        placeholder="Account name"
        maxLength={255}
        value={name}
        error={nameError}
        onChange={(value) => {
          setName(value);
          if (nameError) setNameError("");
        }}
      />
      <div className="flex justify-end gap-2">
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          className="w-[125px]"
          onClick={onClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          className="w-[125px]"
          onClick={handleSubmit}
          loading={isPending}
          disabled={!name.trim() || name.trim() === account?.name}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};
